// Self-hosted, dependency-free visitor identification.
//
// Replaces ThumbmarkJS. Runs entirely in the browser using built-in Web APIs
// and is served from our own app — no CDN, no third-party API, no cost.
//
// Two layers of identity:
//
//   1. A first-party visitor id (a random GUID stored in localStorage + cookie).
//      This is the PRIMARY, stable identifier and is matched first server-side
//      (CustomerFingerprint#find_customer_for_website). It survives canvas/GPU
//      drift, browser zoom, display changes, and browser updates — anything that
//      perturbs the device fingerprint — because it doesn't depend on any device
//      signal at all. It only resets if the visitor clears site storage.
//
//   2. A device fingerprint hashed from stable-ish device/browser signals
//      (canvas, WebGL, screen, locale, hardware, fonts). This is the SECONDARY
//      fallback used to re-link a returning visitor who cleared storage. It keeps
//      higher-entropy signals (canvas/WebGL) on purpose: as a fallback, a missed
//      match merely creates a new customer record, whereas dropping entropy would
//      risk merging two different people into one chat. Volatile signals that add
//      little entropy (page-zoom-sensitive devicePixelRatio, the user-reorderable
//      navigator.languages list, the update-sensitive WebGL shading-language
//      version) are deliberately excluded for stability.
//
// The fingerprint logic is mirrored verbatim into public/embed/v1/ai-mode.js
// (which can't use import maps). Keep the two in sync — if the component set or
// hash here changes, change it there too, or the embed and the in-app iframe will
// compute different fingerprints for the same visitor.

const VISITOR_ID_KEY = "nm_visitor_id"
const VISITOR_ID_TTL_DAYS = 730 // ~2 years

// --- Cookie helpers ---------------------------------------------------------

function setCookie(name, value, days) {
  try {
    let expires = ""
    if (days) {
      const date = new Date()
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      expires = "; expires=" + date.toUTCString()
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax"
  } catch (e) {
    /* ignore */
  }
}

function getCookie(name) {
  try {
    const nameEQ = name + "="
    const parts = document.cookie.split(";")
    for (let i = 0; i < parts.length; i++) {
      let c = parts[i]
      while (c.charAt(0) === " ") c = c.substring(1)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length)
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

// --- First-party visitor id (primary, stable identity) ---------------------

function randomGuid() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID()
    }
  } catch (e) {
    /* fall through */
  }

  // RFC4122 v4 fallback. crypto.getRandomValues is available even in insecure
  // contexts (unlike crypto.randomUUID/subtle); Math.random is the last resort.
  let bytes
  try {
    if (window.crypto && window.crypto.getRandomValues) {
      bytes = window.crypto.getRandomValues(new Uint8Array(16))
    }
  } catch (e) {
    bytes = null
  }
  if (!bytes) {
    bytes = new Uint8Array(16)
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  let hex = ""
  for (let i = 0; i < 16; i++) hex += bytes[i].toString(16).padStart(2, "0")
  return (
    hex.slice(0, 8) + "-" + hex.slice(8, 12) + "-" + hex.slice(12, 16) + "-" +
    hex.slice(16, 20) + "-" + hex.slice(20)
  )
}

// Returns a stable per-browser visitor id, generating and persisting one on
// first use. Stored in both localStorage and a long-lived cookie for resilience.
export function getVisitorId() {
  let id = null
  try {
    id = window.localStorage.getItem(VISITOR_ID_KEY)
  } catch (e) {
    /* ignore */
  }
  if (!id) id = getCookie(VISITOR_ID_KEY)
  if (!id) id = randomGuid()

  try {
    window.localStorage.setItem(VISITOR_ID_KEY, id)
  } catch (e) {
    /* ignore */
  }
  setCookie(VISITOR_ID_KEY, id, VISITOR_ID_TTL_DAYS)
  return id
}

// --- Device fingerprint signal collectors -----------------------------------

function getCanvasFingerprint() {
  try {
    const canvas = document.createElement("canvas")
    canvas.width = 280
    canvas.height = 60
    const ctx = canvas.getContext("2d")
    if (!ctx) return "no-canvas"

    // Text rendering varies by font rasterizer / anti-aliasing / OS.
    ctx.textBaseline = "top"
    ctx.font = "14px 'Arial'"
    ctx.fillStyle = "#f60"
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = "#069"
    ctx.fillText("NinjaCat AI \u{1F600} 0123456789", 2, 15)
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)"
    ctx.fillText("NinjaCat AI \u{1F600} 0123456789", 4, 17)

    // Geometry + compositing exercises the GPU rasterization path.
    ctx.globalCompositeOperation = "multiply"
    ctx.fillStyle = "rgb(255,0,255)"
    ctx.beginPath()
    ctx.arc(50, 30, 25, 0, Math.PI * 2, true)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = "rgb(0,255,255)"
    ctx.beginPath()
    ctx.arc(100, 30, 25, 0, Math.PI * 2, true)
    ctx.closePath()
    ctx.fill()

    return canvas.toDataURL()
  } catch (e) {
    return "canvas-error"
  }
}

function getWebglFingerprint() {
  try {
    const canvas = document.createElement("canvas")
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl")
    if (!gl) return "no-webgl"

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info")
    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR)
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER)

    // Numeric capability limits are stable for a given GPU. The supported-
    // extension list is sorted so its enumeration order (which is not
    // guaranteed) doesn't perturb the fingerprint; SHADING_LANGUAGE_VERSION is
    // intentionally omitted as it shifts on routine browser/driver updates.
    const extensions = (gl.getSupportedExtensions() || []).slice().sort().join(",")
    const params = [
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      gl.getParameter(gl.MAX_VARYING_VECTORS),
      gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      extensions
    ].join("|")

    return [vendor, renderer, params].join("~")
  } catch (e) {
    return "webgl-error"
  }
}

function getFontFingerprint() {
  try {
    if (!document.body) return "fonts-nobody"

    const baseFonts = ["monospace", "sans-serif", "serif"]
    const testString = "mmmmmmmmmmlli"
    const testSize = "72px"
    const testFonts = [
      "Arial", "Arial Black", "Arial Narrow", "Calibri", "Cambria",
      "Comic Sans MS", "Consolas", "Courier New", "Georgia", "Helvetica",
      "Impact", "Lucida Console", "Palatino Linotype", "Roboto",
      "Segoe UI", "Tahoma", "Times New Roman", "Trebuchet MS", "Verdana"
    ]

    const span = document.createElement("span")
    span.style.position = "absolute"
    span.style.left = "-9999px"
    span.style.top = "-9999px"
    span.style.fontSize = testSize
    span.style.lineHeight = "normal"
    span.textContent = testString
    document.body.appendChild(span)

    const baseWidths = {}
    const baseHeights = {}
    for (const base of baseFonts) {
      span.style.fontFamily = base
      baseWidths[base] = span.offsetWidth
      baseHeights[base] = span.offsetHeight
    }

    const available = []
    for (const font of testFonts) {
      let detected = false
      for (const base of baseFonts) {
        span.style.fontFamily = "'" + font + "'," + base
        if (
          span.offsetWidth !== baseWidths[base] ||
          span.offsetHeight !== baseHeights[base]
        ) {
          detected = true
          break
        }
      }
      if (detected) available.push(font)
    }

    document.body.removeChild(span)
    return available.join(",")
  } catch (e) {
    return "fonts-error"
  }
}

function collectComponents() {
  const nav = navigator || {}
  const scr = window.screen || {}

  let timezone = ""
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ""
  } catch (e) {
    timezone = ""
  }

  // Insertion order is stable, so JSON.stringify is deterministic.
  // Excluded on purpose (volatile, low marginal entropy): the numeric timezone
  // offset (daylight saving), navigator.languages (user-reorderable list — the
  // primary language is kept), and window.devicePixelRatio (changes on zoom and
  // when a window moves between displays of differing DPI).
  return {
    language: nav.language || "",
    platform: nav.platform || "",
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    deviceMemory: nav.deviceMemory || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    timezone: timezone,
    screenResolution: [scr.width || 0, scr.height || 0].join("x"),
    colorDepth: scr.colorDepth || 0,
    touchSupport: "ontouchstart" in window ? 1 : 0,
    canvas: getCanvasFingerprint(),
    webgl: getWebglFingerprint(),
    fonts: getFontFingerprint()
  }
}

// --- Hashing ----------------------------------------------------------------

// cyrb128: a fast, well-distributed 128-bit non-crypto hash. Chosen over
// SubtleCrypto SHA-256 deliberately: it is synchronous and works in EVERY
// context (SubtleCrypto is only exposed in secure contexts), so the same input
// always maps to the same id whether this runs on our HTTPS app or an embed on
// a non-secure storefront. Non-crypto hashing is standard for fingerprinting —
// this is an identity bucket, not a security primitive.
function cyrb128(str) {
  let h1 = 1779033703
  let h2 = 3144134277
  let h3 = 1013904242
  let h4 = 2773480762
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i)
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067)
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233)
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213)
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179)
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067)
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233)
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213)
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179)
  h1 ^= h2 ^ h3 ^ h4
  h2 ^= h1
  h3 ^= h1
  h4 ^= h1
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0]
}

function hashString(str) {
  const parts = cyrb128(str)
  let hex = ""
  for (let i = 0; i < parts.length; i++) {
    hex += parts[i].toString(16).padStart(8, "0")
  }
  return hex // 32 hex chars (128-bit), identical across all browser contexts
}

// --- Public API -------------------------------------------------------------

// Returns a stable hex fingerprint string for the current device/browser.
// Async for call-site compatibility; never throws (individual signal collectors
// degrade to a deterministic placeholder rather than failing).
export async function generateFingerprint() {
  const components = collectComponents()
  return hashString(JSON.stringify(components))
}

export function detectDeviceType() {
  const ua = (navigator.userAgent || "").toLowerCase()
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/i.test(ua)) {
    return "tablet"
  }
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
    return "mobile"
  }
  return "desktop"
}
